import { Alert, SegmentedControl, Stack, Tooltip } from '@common/ui'
import { useEffect, useMemo, useState } from 'react'
import FormContentInputAdapter from './FormContentInputAdapter'
import PayloadEditor from '../asset/PayloadEditor'
import { safeDecodeFormContentYaml } from './formContentYaml'

type Mode = 'visual' | 'yaml'

type FormPayloadEditorProps = {
  yaml: string
  editable: boolean
  onChange?: (yaml: string) => void
  jsonSchema?: Record<string, unknown>
}

/**
 * The Form's payload editor: a single YAML string presented either as the visual page editor or as the raw YAML, with
 * a toggle between them. Both views read and write the same string, so either edit flows through to save and preview.
 * Visual mode cannot render YAML that doesn't decode into pages, so that toggle is disabled (and undecodable content
 * opens in YAML mode) rather than silently dropping the user's text.
 */
const FormPayloadEditor = ({ yaml, editable, onChange, jsonSchema }: FormPayloadEditorProps) => {
  const decodeError = useMemo(() => safeDecodeFormContentYaml(yaml).error, [yaml])
  const [mode, setMode] = useState<Mode>(() => (safeDecodeFormContentYaml(yaml).error ? 'yaml' : 'visual'))

  // External changes (or YAML edits) can make the content undecodable while visual mode is active; drop to YAML so the
  // user sees and can fix the raw text instead of an empty editor.
  useEffect(() => {
    if (decodeError && mode === 'visual') setMode('yaml')
  }, [decodeError, mode])

  return (
    <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
      <Tooltip label="Fix YAML errors to use the visual editor" disabled={!decodeError}>
        <SegmentedControl
          size="sm"
          value={mode}
          onChange={(next) => setMode(next as Mode)}
          data={[
            { value: 'visual', label: 'Visual', disabled: !!decodeError },
            { value: 'yaml', label: 'YAML' },
          ]}
          style={{ alignSelf: 'flex-start', flexShrink: 0 }}
        />
      </Tooltip>
      {mode === 'visual' ? (
        // Long forms scroll inside this panel (as the YAML editor already does) so the page itself never grows.
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <FormContentInputAdapter yaml={yaml} editable={editable} onYamlChange={onChange} />
        </div>
      ) : (
        <>
          <PayloadEditor value={yaml} onChange={onChange} editable={editable} jsonSchema={jsonSchema} />
          {decodeError && <Alert severity="negative">{decodeError}</Alert>}
        </>
      )}
    </Stack>
  )
}

export default FormPayloadEditor
